import { patientApi } from "@mgdx/api/lib/patientApi";
import { Patient } from "@mgdx/shared/src/models/Patient";
import { getAccessTokenUid, getFirebaseAuth } from "@mgdx-libs/firebase";
import { QueryClient, queryOptions } from "@tanstack/react-query";

import { patientKeys } from "../../factories/patient";
import { setCurrentUser } from "../../stores/currentUserStore";

export const currentPatientQueryOptions = queryOptions<Patient, Response>({
  queryKey: patientKeys.patient.current.queryKey,
  queryFn: () => patientApi.getCurrentPatient(),
});

type RefreshCurrentPatientArgs = {
  queryClient: QueryClient;
};

/**
 * 患者情報を取得し、家族アカウントの一覧などが参照するキャッシュと currentUser の両方に反映する。
 */
export const refreshCurrentPatient = async ({ queryClient }: RefreshCurrentPatientArgs) => {
  const updatingPatient = startUpdatingPatient();
  // 更新前に始まった取得に相乗りすると更新前の値を反映してしまうため、取得中なら止めてから取り直す
  await queryClient.cancelQueries({ queryKey: currentPatientQueryOptions.queryKey });
  const patient = await queryClient.fetchQuery(currentPatientQueryOptions);
  // 取得中に別の患者へ切り替わった場合は、前の患者の情報を書き込まない
  if (isSamePatient(updatingPatient)) setCurrentUser(patient);
  return patient;
};

/**
 * 更新を始めた患者。リクエストに使うトークンの持ち主で表す。
 * Firebase のログイン状態の復元前もトークンで判定でき、ログアウト・切り替えではトークンが消えるか入れ替わる。
 * トークンがない場合(保存前・保存できない環境)は、401 の後にログイン中の患者のトークンで再送されるため、その患者で表す。
 */
export type UpdatingPatient = { uid?: string };

const getRequestingPatientUid = () => getAccessTokenUid() ?? getFirebaseAuth()?.currentUser?.uid;

export const startUpdatingPatient = (): UpdatingPatient => ({ uid: getRequestingPatientUid() });

/**
 * 更新を始めたときと同じ患者のままかを判定する。切り替え後に届いた前の患者の応答を書き込まないために使う。
 */
export const isSamePatient = (updatingPatient: UpdatingPatient | undefined) =>
  !!updatingPatient && getRequestingPatientUid() === updatingPatient.uid;
